import { Link } from "react-router-dom";
import { Users, Target, Calendar, Lock } from "lucide-react";
import { Progress } from "./ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { fileUrl, formatXOF } from "../lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=70";

export default function CagnotteCard({ cagnotte }) {
  const pct = cagnotte.goal_amount
    ? Math.min(100, Math.round((cagnotte.current_amount / cagnotte.goal_amount) * 100))
    : 0;

  const img = cagnotte.image_url ? fileUrl(cagnotte.image_url) : FALLBACK;

  return (
    <Link
      to={`/cagnotte/${cagnotte.id}`}
      className="group card-soft overflow-hidden flex flex-col block"
      data-testid={`cagnotte-card-${cagnotte.id}`}
    >
      <div className="relative aspect-[16/10] max-h-72 overflow-hidden bg-secondary">
        <img
          src={img}
          alt={cagnotte.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
        />
        {cagnotte.visibility === "private" && (
          <Badge className="absolute top-3 right-3 bg-black/70 text-white border-0">
            <Lock className="h-3 w-3 mr-1" /> Privée
          </Badge>
        )}
        {cagnotte.category && (
          <Badge className="absolute top-3 left-3 bg-white/90 text-foreground border-0">
            {cagnotte.category}
          </Badge>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {cagnotte.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
          {cagnotte.description}
        </p>

        <div className="flex items-center gap-2 mb-4">
          <Avatar className="h-7 w-7">
            <AvatarImage src={cagnotte.creator_picture} />
            <AvatarFallback className="gradient-gold-green text-white text-xs">
              {cagnotte.creator_name?.[0] || "?"}
            </AvatarFallback>
          </Avatar>
          <span className="text-xs text-muted-foreground truncate">par {cagnotte.creator_name || "Anonyme"}</span>
        </div>

        {cagnotte.goal_amount ? (
          <>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-sm font-semibold gradient-text">{formatXOF(cagnotte.current_amount)}</span>
              <span className="text-xs text-muted-foreground">/ {formatXOF(cagnotte.goal_amount)}</span>
            </div>
            <Progress value={pct} className="h-2 [&>div]:gradient-gold-green" />
          </>
        ) : (
          <div className="text-sm font-semibold gradient-text">{formatXOF(cagnotte.current_amount)} collectés</div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground mt-3 pt-3 border-t">
          <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {cagnotte.contributions_count || 0} participant·s</span>
          {cagnotte.end_date && (
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(cagnotte.end_date).toLocaleDateString("fr-FR")}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
